import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  ChevronUp, ChevronDown, FileText, ShoppingBag, Info,
  ChevronLeft, ChevronRight,
} from 'lucide-react';
import { useTheme } from '../../core/ui/ThemeContext';
import { useLanguage } from '../../core/i18n/LanguageContext';
import { formatQuantity } from 'core/utils/units';
import { getLagerMaterialDetail } from 'core/api/apiService';
import MaterialDetail from './MaterialDetail';
import lagerNavState from './lagerNavState';

/**
 * Ergebnistabelle fuer das Lager-Modul (Phase 2).
 * Klick auf eine Zeile oeffnet eine Inline-Detailzeile darunter (Phase 2).
 * Nur eine Zeile gleichzeitig offen; Escape / X / Klick auf dieselbe schliesst.
 *
 * Props:
 *   items              Array<ItemRow>
 *   total              number
 *   page               number
 *   perPage            number
 *   totalPages         number
 *   sort               string  "col:dir"
 *   onSort             (colKey: string) => void
 *   onPageChange       (page: number) => void
 *   onPerPageChange    (perPage: number) => void
 *   loading            boolean
 *   error              string|null
 *   user               Object          Fuer Detailabruf + Warenkorb
 *   werk               string          Aktuell gewaehltes Werk
 *   showSuccess        (msg) => void
 *   showError          (msg) => void
 *   onCartUpdated      () => void
 *   onNavigateToCreateBanf (prefill) => void
 */
const MaterialTable = ({
  items,
  total,
  page,
  perPage,
  totalPages,
  sort,
  onSort,
  onPageChange,
  onPerPageChange,
  loading,
  error,
  user,
  werk,
  showSuccess,
  showError,
  onCartUpdated,
  onNavigateToCreateBanf,
}) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();

  // ---- Expansion State ----
  // expandedMatnr wird in lagerNavState gespiegelt, damit die offene Zeile nach
  // einem Round-Trip (Lager -> Warenkorb -> Zurück) wiederhergestellt werden kann.
  const [expandedMatnr, setExpandedMatnrState] = useState(null);
  const setExpandedMatnr = useCallback((m) => {
    lagerNavState.expandedMatnr = m;
    setExpandedMatnrState(m);
  }, []);
  // Beim Mount gemerkte offene Zeile (einmaliger Restore, sobald die Liste geladen ist)
  const pendingRestoreRef = useRef(lagerNavState.expandedMatnr || null);
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);

  // Sortier-Whitelist gemaess Backend-Spec
  const SORTABLE = ['matnr', 'maktx', 'matkl', 'mtart', 'labst', 'minbe', 'last_net_price', 'last_vendor_name'];

  const [sortCol, sortDir] = sort ? sort.split(':') : ['matnr', 'asc'];


  const formatPrice = (price, currency) => {
    if (price == null || price === '') return '—';
    const num = parseFloat(price);
    if (Number.isNaN(num)) return '—';
    const fmt = new Intl.NumberFormat('de-DE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
    return currency ? `${fmt} ${currency}` : fmt;
  };

  const columns = [
    { key: 'matnr', label: t('lm.colMatnr'), sortKey: 'matnr', width: 'w-32', mono: true },
    { key: 'maktx', label: t('lm.colMaktx'), sortKey: 'maktx', flex: true },
    { key: 'wgbez', label: t('lm.colWarengruppe'), sortKey: 'matkl', width: 'w-36' },
    { key: 'mtart', label: t('lm.colMaterialart'), sortKey: 'mtart', width: 'w-20' },
    { key: 'meins', label: t('lm.colMe'), sortKey: null, width: 'w-14', align: 'center' },
    { key: 'frei_verf_bestand', label: t('lm.colBestand'), sortKey: 'labst', width: 'w-28', align: 'right' },
    { key: 'meldebestand', label: t('lm.colMeldebestand'), sortKey: 'minbe', width: 'w-28', align: 'right' },
    { key: 'letzter_preis', label: t('lm.colPreis'), sortKey: 'last_net_price', width: 'w-32', align: 'right' },
    { key: 'letzter_lieferant', label: t('lm.colLieferant'), sortKey: 'last_vendor_name', width: 'w-40' },
    { key: 'status', label: t('lm.colStatus'), sortKey: null, width: 'w-20', align: 'center' },
  ];

  const headerCellClass = (col) => {
    const base = `px-3 py-3 text-xs font-semibold uppercase tracking-wide whitespace-nowrap select-none ${col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left'} ${isDarkMode ? 'text-white/50' : 'text-slate-500'}`;
    const sortable = col.sortKey ? 'cursor-pointer hover:text-brand transition-colors' : '';
    return `${base} ${sortable}`.trim();
  };

  const SortIcon = ({ colKey }) => {
    if (!SORTABLE.includes(colKey)) return null;
    if (sortCol !== colKey) return <ChevronUp className="inline w-3 h-3 opacity-20 ml-0.5" />;
    return sortDir === 'asc'
      ? <ChevronUp className="inline w-3 h-3 ml-0.5 text-brand" />
      : <ChevronDown className="inline w-3 h-3 ml-0.5 text-brand" />;
  };

  // Status-Icons: neutral, mit Tooltip
  const StatusIcons = ({ status }) => {
    if (!status) return null;
    return (
      <div className="flex items-center justify-center gap-1">
        {status.open_banf && (
          <span title={t('lm.iconOpenBanf')} className="cursor-default">
            <FileText className="w-4 h-4 text-blue-400" />
          </span>
        )}
        {status.open_order && (
          <span title={t('lm.iconOpenOrder')} className="cursor-default">
            <ShoppingBag className="w-4 h-4 text-emerald-500" />
          </span>
        )}
        {status.dispo_active && (
          <span title={t('lm.iconDispo')} className="cursor-default">
            <Info className="w-4 h-4 text-slate-400" />
          </span>
        )}
      </div>
    );
  };

  // ---- Detail laden ----
  const loadDetail = useCallback(async (matnr) => {
    setDetailData(null);
    setDetailError(null);
    setDetailLoading(true);
    try {
      const data = await getLagerMaterialDetail(user, matnr, werk);
      setDetailData(data);
    } catch (err) {
      setDetailError(err.message || t('lm.errorDetail'));
    } finally {
      setDetailLoading(false);
    }
  }, [user, werk]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Zeile aufklappen / zuklappen ----
  const handleRowClick = (matnr) => {
    if (expandedMatnr === matnr) {
      // Schliessen
      setExpandedMatnr(null);
      setDetailData(null);
      setDetailError(null);
    } else {
      setExpandedMatnr(matnr);
      loadDetail(matnr);
    }
  };

  // ---- Escape schliesst Detail ----
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && expandedMatnr) {
        setExpandedMatnr(null);
        setDetailData(null);
        setDetailError(null);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [expandedMatnr]);

  // ---- Seiten-/Filter-Wechsel schliesst offene Details ----
  // Ausnahme: einmaliger Restore der gemerkten Zeile nach Remount, sobald die
  // Liste geladen ist und die Zeile enthält (Lager -> Warenkorb -> Zurück).
  useEffect(() => {
    if (pendingRestoreRef.current) {
      const target = pendingRestoreRef.current;
      if (Array.isArray(items) && items.length > 0) {
        pendingRestoreRef.current = null;
        if (items.some((it) => it.matnr === target)) {
          setExpandedMatnr(target);
          loadDetail(target);
          return;
        }
      } else {
        // Liste noch nicht geladen -> warten, gemerkte Zeile NICHT verwerfen
        return;
      }
    }
    setExpandedMatnr(null);
    setDetailData(null);
    setDetailError(null);
  }, [items]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Loading State ---
  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center py-20">
        <div className="text-center">
          <div className={`w-8 h-8 border-2 border-brand border-t-transparent rounded-full animate-spin mx-auto mb-3`}></div>
          <p className={`text-sm ${isDarkMode ? 'text-white/50' : 'text-slate-500'}`}>{t('lm.loading')}</p>
        </div>
      </div>
    );
  }

  // --- Error State ---
  if (error) {
    return (
      <div className="flex-1 flex items-center justify-center py-20">
        <div className="text-center max-w-sm">
          <div className={`w-12 h-12 rounded-2xl flex items-center justify-center mx-auto mb-3 ${isDarkMode ? 'bg-red-500/20' : 'bg-red-50'}`}>
            <Info className={`w-6 h-6 ${isDarkMode ? 'text-red-400' : 'text-red-500'}`} />
          </div>
          <p className={`text-sm font-medium ${isDarkMode ? 'text-white/80' : 'text-slate-700'}`}>{t('lm.error')}</p>
          <p className={`text-xs mt-1 ${isDarkMode ? 'text-white/40' : 'text-slate-500'}`}>{error}</p>
        </div>
      </div>
    );
  }

  // --- Empty State ---
  if (!Array.isArray(items) || items.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center py-20">
        <div className="text-center max-w-sm">
          <div className={`w-12 h-12 rounded-2xl flex items-center justify-center mx-auto mb-3 ${isDarkMode ? 'bg-white/8' : 'bg-slate-100'}`}>
            <FileText className={`w-6 h-6 ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`} />
          </div>
          <p className={`text-sm font-medium ${isDarkMode ? 'text-white/70' : 'text-slate-600'}`}>{t('lm.empty')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-0">
      {/* ---- Tabelle ---- */}
      <div className="overflow-x-auto">
        <table className="w-full min-w-max border-collapse">
          <thead>
            <tr className={`border-b ${isDarkMode ? 'border-white/10 bg-gray-900/60' : 'border-slate-200 bg-slate-50'}`}>
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={`${headerCellClass(col)} ${col.width || ''} ${col.flex ? 'min-w-[160px]' : ''}`}
                  onClick={col.sortKey ? () => onSort(col.sortKey) : undefined}
                >
                  {col.label}
                  {col.sortKey && <SortIcon colKey={col.sortKey} />}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.isArray(items) && items.map((row, idx) => {
              const matnr = row.matnr ?? String(idx);
              const isExpanded = expandedMatnr === matnr;
              const isZebra = idx % 2 !== 0;
              const rowBase = `border-b transition-colors cursor-pointer select-none ${isDarkMode
                ? `border-white/5 ${isZebra ? 'bg-white/[0.02]' : ''} hover:bg-brand/8 ${isExpanded ? 'bg-brand/12' : ''}`
                : `border-slate-100 ${isZebra ? 'bg-slate-50/60' : 'bg-white'} hover:bg-brand/5 ${isExpanded ? 'bg-brand/5' : ''}`
              }`;
              const cellClass = `px-3 py-2.5 text-sm ${isDarkMode ? 'text-white/80' : 'text-slate-700'}`;
              const cellRight = `px-3 py-2.5 text-sm text-right ${isDarkMode ? 'text-white/80' : 'text-slate-700'}`;
              const cellCenter = `px-3 py-2.5 text-sm text-center ${isDarkMode ? 'text-white/80' : 'text-slate-700'}`;

              return (
                <React.Fragment key={row.id ?? matnr}>
                  <tr
                    className={rowBase}
                    onClick={() => handleRowClick(matnr)}
                    role="button"
                    tabIndex={0}
                    aria-expanded={isExpanded}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleRowClick(matnr);
                      }
                    }}
                  >
                    {/* Materialnummer */}
                    <td className={`${cellClass} font-mono text-xs`}>{row.matnr ?? '—'}</td>
                    {/* Kurztext */}
                    <td className={`${cellClass} max-w-xs`}>
                      <span className="block truncate" title={row.maktx}>{row.maktx ?? '—'}</span>
                      {row.bismt && (
                        <span className={`text-[11px] block truncate ${isDarkMode ? 'text-white/35' : 'text-slate-400'}`}>
                          {row.bismt}
                        </span>
                      )}
                    </td>
                    {/* Warengruppe */}
                    <td className={`${cellClass} text-xs truncate max-w-[150px]`} title={row.wgbez}>
                      {row.wgbez || row.matkl || '—'}
                    </td>
                    {/* Materialart */}
                    <td className={cellCenter}>
                      <span className={`inline-block px-1.5 py-0.5 rounded text-[11px] font-mono ${isDarkMode ? 'bg-white/8 text-white/60' : 'bg-slate-100 text-slate-500'}`}>
                        {row.mtart ?? '—'}
                      </span>
                    </td>
                    {/* ME */}
                    <td className={`${cellCenter} text-xs font-mono ${isDarkMode ? 'text-white/50' : 'text-slate-400'}`}>
                      {row.meins ?? '—'}
                    </td>
                    {/* Frei verf. Bestand (eigen + Konsignation) */}
                    <td className={cellRight}>
                      {row.frei_verf_bestand != null
                        ? formatQuantity(row.frei_verf_bestand, row.meins)
                        : '—'}
                      {row.frei_konsignation > 0 && (
                        <span className={`block text-[10px] ${isDarkMode ? 'text-amber-300/70' : 'text-amber-600'}`}>
                          {t('lm.colDavonKonsi')} {formatQuantity(row.frei_konsignation, row.meins)}
                        </span>
                      )}
                      {row.frei_at_vendor > 0 && (
                        <span className={`block text-[10px] ${isDarkMode ? 'text-violet-300/70' : 'text-violet-600'}`}>
                          {t('lm.colBeimLieferant')} {formatQuantity(row.frei_at_vendor, row.meins)}
                        </span>
                      )}
                    </td>
                    {/* Meldebestand */}
                    <td className={`${cellRight} ${isDarkMode ? 'text-white/50' : 'text-slate-400'}`}>
                      {row.meldebestand != null
                        ? formatQuantity(row.meldebestand, row.meins)
                        : '—'}
                    </td>
                    {/* Letzter Preis */}
                    <td className={`${cellRight} text-xs`}>
                      {formatPrice(row.letzter_preis, row.letzter_preis_waehrung)}
                    </td>
                    {/* Letzter Lieferant */}
                    <td className={`${cellClass} text-xs truncate max-w-[160px]`} title={row.letzter_lieferant}>
                      {row.letzter_lieferant ?? '—'}
                    </td>
                    {/* Status */}
                    <td className={cellCenter}>
                      <StatusIcons status={row.status} />
                    </td>
                  </tr>

                  {/* ---- Inline-Detailzeile ---- */}
                  {isExpanded && (
                    <MaterialDetail
                      data={detailData}
                      loading={detailLoading}
                      error={detailError}
                      colSpan={columns.length}
                      onClose={() => {
                        setExpandedMatnr(null);
                        setDetailData(null);
                        setDetailError(null);
                      }}
                      user={user}
                      werk={werk}
                      showSuccess={showSuccess}
                      showError={showError}
                      onCartUpdated={onCartUpdated}
                      onNavigateToCreateBanf={onNavigateToCreateBanf}
                      onReload={() => loadDetail(row.matnr)}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ---- Pagination ---- */}
      <div className={`flex items-center justify-between px-4 py-3 border-t text-sm ${isDarkMode
        ? 'border-white/10 bg-gray-900/40'
        : 'border-slate-200 bg-white'
      }`}>
        {/* Links: per_page + Trefferzahl */}
        <div className="flex items-center gap-3">
          <span className={`text-xs ${isDarkMode ? 'text-white/40' : 'text-slate-500'}`}>
            {t('lm.perPage')}:
          </span>
          {[25, 50, 100].map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => onPerPageChange(n)}
              className={`px-2 py-0.5 rounded text-xs font-medium transition-colors ${perPage === n
                ? isDarkMode ? 'bg-brand/30 text-blue-200 border border-brand/40' : 'bg-brand text-white'
                : isDarkMode ? 'text-white/50 hover:text-white/80' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              {n}
            </button>
          ))}
          <span className={`ml-2 text-xs ${isDarkMode ? 'text-white/40' : 'text-slate-500'}`}>
            {t('lm.resultsCount', { count: new Intl.NumberFormat('de-DE').format(total ?? 0) })}
          </span>
        </div>

        {/* Rechts: Seitennavigation */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
            className={`p-1.5 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${isDarkMode
              ? 'hover:bg-white/10 text-white/60'
              : 'hover:bg-slate-100 text-slate-500'
            }`}
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className={`text-xs ${isDarkMode ? 'text-white/60' : 'text-slate-600'}`}>
            {t('lm.page')} {page} / {totalPages || 1}
          </span>
          <button
            type="button"
            disabled={page >= (totalPages || 1)}
            onClick={() => onPageChange(page + 1)}
            className={`p-1.5 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${isDarkMode
              ? 'hover:bg-white/10 text-white/60'
              : 'hover:bg-slate-100 text-slate-500'
            }`}
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default MaterialTable;
